(() => {
  if (customElements.get('magnification-slider')) return;

  class MagnificationSlider extends HTMLElement {
    connectedCallback() {
      this.contentSlides = [...this.querySelectorAll('.magnification-slider__copy [data-mag-slide]')];
      this.previewSlides = [...this.querySelectorAll('.magnification-slider__preview [data-mag-slide]')];
      this.slides = this.contentSlides;
      this.labels = [...this.querySelectorAll('[data-mag-label]')];
      this.ruler = this.querySelector('[data-mag-ruler]');
      this.track = this.querySelector('[data-mag-track]');
      this.needle = this.querySelector('[data-mag-needle]');
      this.visual = this.querySelector('.magnification-slider__card');
      this.liveRegion = this.querySelector('[data-mag-live]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.index = Math.max(
        0,
        this.contentSlides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      if (this.index < 0) this.index = 0;
      this.dragging = false;
      this.pointerId = null;
      this.wheelDelta = 0;
      this.wheelCooling = false;

      this.setIndex(this.index, { announce: false, animate: false });
      this.bindEvents();
    }

    disconnectedCallback() {
      if (this.wheelTimer) window.clearTimeout(this.wheelTimer);
      this.unbindEvents();
    }

    bindEvents() {
      this.onLabelClick = (event) => {
        const button = event.target.closest('[data-mag-label]');
        if (!button) return;
        const next = Number(button.dataset.magLabel);
        if (Number.isNaN(next)) return;
        this.setIndex(next, { announce: true });
      };

      this.onKeyDown = (event) => {
        if (!this.contains(document.activeElement) && document.activeElement !== this) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          this.setIndex(Math.min(this.contentSlides.length - 1, this.index + 1), { announce: true });
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.setIndex(Math.max(0, this.index - 1), { announce: true });
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.setIndex(0, { announce: true });
        } else if (event.key === 'End') {
          event.preventDefault();
          this.setIndex(this.contentSlides.length - 1, { announce: true });
        }
      };

      this.onPointerDown = (event) => {
        if (!this.track || event.button === 2) return;
        this.dragging = true;
        this.pointerId = event.pointerId;
        this.track.setPointerCapture?.(event.pointerId);
        this.classList.add('is-dragging');
        this.updateFromPointer(event.clientX, { announce: false });
      };

      this.onPointerMove = (event) => {
        if (!this.dragging || (this.pointerId != null && event.pointerId !== this.pointerId)) return;
        this.updateFromPointer(event.clientX, { announce: false });
      };

      this.onPointerUp = (event) => {
        if (!this.dragging || (this.pointerId != null && event.pointerId !== this.pointerId)) return;
        this.dragging = false;
        this.pointerId = null;
        this.classList.remove('is-dragging');
        this.track?.releasePointerCapture?.(event.pointerId);
        this.setIndex(this.index, { announce: true });
      };

      this.onResize = () => this.updateNeedle({ animate: false });

      this.onVisualWheel = (event) => {
        if (this.contentSlides.length < 2) return;

        const scrollingDown = event.deltaY > 0;
        const atStart = this.index <= 0;
        const atEnd = this.index >= this.contentSlides.length - 1;

        if ((scrollingDown && atEnd) || (!scrollingDown && atStart)) {
          this.wheelDelta = 0;
          return;
        }

        event.preventDefault();

        if (this.wheelCooling) return;

        this.wheelDelta += event.deltaY;
        const threshold = 48;
        if (Math.abs(this.wheelDelta) < threshold) return;

        const direction = this.wheelDelta > 0 ? 1 : -1;
        this.wheelDelta = 0;
        this.wheelCooling = true;
        this.setIndex(this.index + direction, { announce: true });
        if (this.wheelTimer) window.clearTimeout(this.wheelTimer);
        this.wheelTimer = window.setTimeout(() => {
          this.wheelCooling = false;
          this.wheelDelta = 0;
          this.wheelTimer = null;
        }, this.reducedMotion ? 80 : 320);
      };

      this.addEventListener('click', this.onLabelClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.track?.addEventListener('pointerdown', this.onPointerDown);
      this.track?.addEventListener('pointermove', this.onPointerMove);
      this.track?.addEventListener('pointerup', this.onPointerUp);
      this.track?.addEventListener('pointercancel', this.onPointerUp);
      this.visual?.addEventListener('wheel', this.onVisualWheel, { passive: false });
      window.addEventListener('resize', this.onResize);
    }

    unbindEvents() {
      this.removeEventListener('click', this.onLabelClick);
      this.removeEventListener('keydown', this.onKeyDown);
      this.track?.removeEventListener('pointerdown', this.onPointerDown);
      this.track?.removeEventListener('pointermove', this.onPointerMove);
      this.track?.removeEventListener('pointerup', this.onPointerUp);
      this.track?.removeEventListener('pointercancel', this.onPointerUp);
      this.visual?.removeEventListener('wheel', this.onVisualWheel);
      window.removeEventListener('resize', this.onResize);
    }

    updateFromPointer(clientX, { announce }) {
      if (!this.track || this.contentSlides.length < 2) return;
      const rect = this.track.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const next = Math.round(ratio * (this.contentSlides.length - 1));
      this.setIndex(next, { announce, animate: !this.reducedMotion });
    }

    setIndex(index, { announce = false, animate = !this.reducedMotion } = {}) {
      if (!this.contentSlides.length) return;
      this.index = Math.min(this.contentSlides.length - 1, Math.max(0, index));

      this.contentSlides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      // Keep preview nodes in the DOM so opacity can crossfade.
      this.previewSlides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.removeAttribute('hidden');
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.labels.forEach((label, i) => {
        const active = i === this.index;
        label.classList.toggle('is-active', active);
        label.setAttribute('aria-pressed', active ? 'true' : 'false');
        label.tabIndex = active ? 0 : -1;
      });

      this.dataset.activeIndex = String(this.index);
      this.style.setProperty(
        '--mag-progress',
        String(this.index / Math.max(1, this.contentSlides.length - 1))
      );
      if (this.track) {
        this.track.setAttribute('aria-valuenow', String(this.index));
        const mag = this.contentSlides[this.index]?.dataset.magnification;
        if (mag) this.track.setAttribute('aria-valuetext', mag);
      }
      this.updateNeedle({ animate });

      if (announce && this.liveRegion) {
        const mag = this.contentSlides[this.index]?.dataset.magnification || '';
        this.liveRegion.textContent = `Showing ${mag} magnification`;
      }
    }

    updateNeedle({ animate }) {
      if (!this.needle || !this.track || this.contentSlides.length < 2) return;
      const progress = this.index / (this.contentSlides.length - 1);
      if (!animate || this.reducedMotion || this.dragging) {
        this.needle.style.transition = 'none';
      } else {
        this.needle.style.transition = '';
      }
      this.needle.style.left = `${progress * 100}%`;
    }
  }

  customElements.define('magnification-slider', MagnificationSlider);
})();
